import { ExpenseDivisionMethod } from "../pages/add_expenses/AddExpenses";

enum WhoToSplitBetween {
  Everyone = "Everyone",
  SportHouse = "Sport House",
  SportHouseMinusLulu = "Sport House (Without Lulu)",
  BoulderHouse = "Boulder House",
  BoulderHousePlusLulu = "Boulder House (With Lulu)",
  ManualSelection = "Manual Selection",
}

const APP_GROUP_TO_SHARE_GROUP_NAME_MAP: Record<WhoToSplitBetween, string | undefined> = {
  [WhoToSplitBetween.Everyone]: 'All',
  [WhoToSplitBetween.BoulderHouse]: 'Bouldering Housing Costs',
  [WhoToSplitBetween.BoulderHousePlusLulu]: 'Bouldering Costs',
  [WhoToSplitBetween.SportHouse]: 'SCS Housing Costs',
  [WhoToSplitBetween.SportHouseMinusLulu]: 'SCS Activity Costs',
  [WhoToSplitBetween.ManualSelection]: undefined,
};

// Format as YYYY/MM/DD
function formatDate(date: Date) {
  return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
}

export interface SpreadsheetOutput {
  date: string,
  cadTotal: number,
  usdTotal: number,
  description: string,
  whoPaid: string,

  // ONEOF existingShareName or customShare
  existingShareName?: string,
  customShare?: {
    name: string,
    nameToShareMap: Record<string, number>,
  }
}
export default function calculateSpreadsheetOutputFromAppInput(
  whoPaid: string,
  date: Date,
  total: number,
  currency: string,
  description: string,
  whoToSplitBetween: WhoToSplitBetween,
  manualSplitListOfPeople: string[],
  expenseDivisionMethod: ExpenseDivisionMethod,
  expenseDivisionPersonToAmountMap: Record<string, number>
): SpreadsheetOutput | null {
  // The scenarios are:
  // 1. Equal split across an existing group (WhoToSplitBetween isn't ManualSelection)
  // 2. Equal split across a manually entered group (WhoToSplitBetween is ManualSelection)
  // 3. Custom split (WhoToSplitBetween is CUSTOM)

  if (expenseDivisionMethod === ExpenseDivisionMethod.Equal) {
    if (whoToSplitBetween !== WhoToSplitBetween.ManualSelection) {
      // Equal split across an existing group. This is the easy case :)

      // Since it's not a manual selection, we can re-use a share group from the sheet.
      const shareGroupName = APP_GROUP_TO_SHARE_GROUP_NAME_MAP[whoToSplitBetween];

      return {
        date: formatDate(date),
        cadTotal: currency === 'CAD' ? total : 0,
        usdTotal: currency === 'USD' ? total : 0,
        description,
        whoPaid,
        existingShareName: shareGroupName,
      }
    } else {
      // Equal split across a manually entered group. Create a new share group since the percentages will be different.

      // Note that the share in the nameToShareMap is a percentage of the total from 0 to 1.

      const totalPeople = manualSplitListOfPeople.length;
      const equalAmount = 1.0 / totalPeople;

      const nameToShareMap: Record<string, number> = {};
      manualSplitListOfPeople.forEach(person => {
        nameToShareMap[person] = equalAmount;
      });

      return {
        date: formatDate(date),
        cadTotal: currency === 'CAD' ? total : 0,
        usdTotal: currency === 'USD' ? total : 0,
        description,
        whoPaid,
        customShare: {
          // Add a random number between 1 and 1000 to the name to avoid conflicts with existing share groups.
          name: `Custom Split (${manualSplitListOfPeople.join(', ')}) [${Math.floor(Math.random() * 1000)}]`,
          nameToShareMap,
        }
      }
    }
  } else {
    // Custom split. Regardless of whoToSplitBetween, create a new share group.
    // Note that there's a "shared" amount in custom splits that needs to be properly distributed among the people.
    // The 'shared' amount is things like tip, tax, etc that should be divided among the people.
    // It could either be divided proprotionally according to the amount each person owes, or equally among everyone.
    // I believe proportionally will be more accurate most of the time (tip and tax are both proportional).
    // To calculate each persons share, we just need to subtract the shared amount from the total, then divide by the amount each person owes.

    const sharedAmount = expenseDivisionPersonToAmountMap["Shared"];
    const totalMinusShared = total - sharedAmount;

    const nameToShareMap: Record<string, number> = {};
    Object.entries(expenseDivisionPersonToAmountMap).forEach(([person, amount]) => {
      if (person === "Shared") {
        return;
      }

      // What percentage of the totalMinusShared is this persons?
      const percentage = amount / totalMinusShared;
      nameToShareMap[person] = percentage;
    });

    return {
      date: formatDate(date),
      cadTotal: currency === 'CAD' ? total : 0,
      usdTotal: currency === 'USD' ? total : 0,
      description,
      whoPaid,
      customShare: {
        // Add a random number between 1 and 1000 to the name to avoid conflicts with existing share groups.
        name: `Custom Split (${Object.keys(expenseDivisionPersonToAmountMap).join(', ')}) [${Math.floor(Math.random() * 1000)}]`,
        nameToShareMap,
      }
    }
  }
}
